import 'react-app-polyfill/ie11';
import 'abortcontroller-polyfill';
import 'whatwg-fetch';
import './tools/appInsightsIngestionGuard';
import 'intersection-observer';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import { GlobalStyle, LcrStyle, ThemeFontImport } from './styleHelpers/global';
import App from './App';
import store from './store';
import { ThemeProvider } from './tools/theme';
import { removeIndexedDB } from './tools/db';
import {
    containsBenignBrowserNoise,
    installBenignBrowserRuntimeGuards,
    isBenignBrowserErrorEvent,
    isBenignBrowserRejection
} from './tools/benignBrowserErrors';
import { isChunkLoadFailure, reloadForChunkLoadFailure } from './tools/chunkLoadRecovery';

// Expose removeIndexedDB function globally for console access (browser-only)
const browserWindow = globalThis.window;
if (browserWindow) {
    (browserWindow as any).removeIndexedDB = removeIndexedDB;
}

function renderApp() {
    const root = ReactDOM.createRoot(
        document.getElementById('react-app') as HTMLElement
    );
    root.render(
        <>
            <LcrStyle />
            <Provider store={store}>
                <ThemeProvider>
                    <ThemeFontImport />
                    <GlobalStyle />
                    <App />
                </ThemeProvider>
            </Provider>
        </>
    );
}

function isBenignWindowOnError(
    message: Event | string,
    source?: string,
    error?: Error | string | null
): boolean {
    const errorObject = error instanceof Error ? error : undefined;
    const errorText = typeof error === 'string' ? error : errorObject?.message;

    return containsBenignBrowserNoise({
        message,
        source,
        errorMessage: errorText,
        stack: errorObject?.stack
    });
}

installBenignBrowserRuntimeGuards();

// When a deploy happens, webpack deletes old chunk files.
// If the user has a stale session and navigates to a lazy-loaded route,
// the browser will fail to load the missing chunk. Auto-reload recovers them.
window.addEventListener('unhandledrejection', (event) => {
    if (isBenignBrowserRejection(event.reason)) {
        event.preventDefault();
        return;
    }

    if (isChunkLoadFailure(event.reason)) {
        reloadForChunkLoadFailure();
    }
});

const previousOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
    if (isBenignWindowOnError(message, source, error)) {
        return true;
    }

    if (typeof previousOnError === 'function') {
        return previousOnError(message, source, lineno, colno, error);
    }

    return false;
};

window.addEventListener('error', (event) => {
    // Browser/DevTools noise (ResizeObserver loops, Live Metrics INP startTime).
    // Capture phase + preventDefault hides Uncaught logs that the previous bubble-only handler missed.
    if (isBenignBrowserErrorEvent(event)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
    }

    if (isChunkLoadFailure(event.error ?? event)) {
        reloadForChunkLoadFailure();
    }
}, true);

renderApp();

console.info(`%c App version: ${__APP_VERSION__} `, 'background:#1976d2;color:#fff;padding:2px 6px;border-radius:3px;');

// Allow Hot Module Replacement
if (module.hot) {
    module.hot.accept('./App', () => {
        renderApp();
    });
}
